import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import type { Dashboard2DayBookingSummary } from "../types/dashboard2-work-status.types";
import {
  dashboard2AdditionalBookingBadgeSx,
  dashboard2AdditionalBookingRowSx
} from "../dashboard2-status.styles";
import { participantTagTranslationKey } from "../utils/dashboard2-booking-participant-tags.utils";
import { useTranslation } from "react-i18next";

type Dashboard2AdditionalBookingsListProps = {
  bookings: Dashboard2DayBookingSummary[];
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
};

export const Dashboard2AdditionalBookingsList: React.FC<Dashboard2AdditionalBookingsListProps> = ({
  bookings,
  onBookingClick
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (bookings.length === 0) {
    return null;
  }

  return (
    <Box data-testid="dashboard2-additional-bookings-list">
      {bookings.map(booking => (
        <Box
          key={booking.id}
          component={onBookingClick ? "button" : "div"}
          type={onBookingClick ? "button" : undefined}
          sx={dashboard2AdditionalBookingRowSx(Boolean(onBookingClick))}
          onClick={onBookingClick ? () => onBookingClick(booking) : undefined}
          data-testid={`dashboard2-additional-booking-${booking.id}`}
        >
          <Typography variant="body2" fontWeight={600} noWrap>
            {booking.primaryLine}
          </Typography>
          {booking.secondaryLine && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {booking.secondaryLine}
            </Typography>
          )}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
            <Typography
              component="span"
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={dashboard2AdditionalBookingBadgeSx(theme)}
            >
              {booking.typeLabel}
            </Typography>
            {booking.participantTags?.map(tag => (
              <Typography
                key={tag}
                component="span"
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={dashboard2AdditionalBookingBadgeSx(theme)}
                data-testid={`dashboard2-additional-booking-tag-${booking.id}-${tag}`}
              >
                {t(participantTagTranslationKey(tag))}
              </Typography>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
